<template>
  <BaseElForm ref="formRef" :model="syncItem" :rules="formRules" label-position="top">
    <BaseElFormItem :label="displayData.formItemLabel.isOverTime">
      <BaseElRadioGroup
        v-model="syncItem.isOverTime"
        @change="onIsOvertimeChange(syncItem)"
      >
        <BaseElRadio :label="true" data-cy="main-overTime-yes">是</BaseElRadio>
        <BaseElRadio :label="false" data-cy="main-overTime-no">否</BaseElRadio>
      </BaseElRadioGroup>
    </BaseElFormItem>

    <p class="font-medium text-gray-100" style="margin-bottom: 8px;"><span class="font-medium" style="color: #F56C6C; margin-right: 4px;">*</span>{{ displayData.formItemLabel.date }}</p>
    <div class="flex" style="gap: 8px">
      <BaseElFormItem class="w-full" prop="date">
        <el-date-picker
          ref="datePickerRef"
          v-model="syncItem.date"
          style="width: 100%"
          editable
          :picker-options="getDatePickerOptions()"
          type="date"
          placeholder="選擇日期"
          @focus="getDateOptions(syncItem)"
          @change="onDateChange(syncItem)"
        />
      </BaseElFormItem>

      <BaseElFormItem class="w-full" prop="time">
        <ElInputWrapper>
          <BaseElSelect
            v-if="showControl.formItem.time.timeSelect"
            v-model="syncItem.time"
            testName="formData_time"
            placeholder="請選擇"
            no-data-text="無數據"
            @change="onTimeChange(syncItem)"
          >
            <BaseElSelectOption
              v-for="item in displayAvailableTimes"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </BaseElSelect>
        </ElInputWrapper>

        <ElInputWrapper>
          <!-- <BaseElSelect
            v-if="showControl.formItem.time.overTimeSelect"
            v-model="syncItem.time"
            testName="formData_time"
            placeholder="請選擇"
            no-data-text="無數據"
            @change="onTimeChange(syncItem)"
          >
            <BaseElSelectOption
              v-for="item in displayOvertimeTimeOptions(syncItem)"
              :key="item.label"
              :label="item.label"
              :value="item.value"
            />
          </BaseElSelect> -->
          <BaseTimePicker
            v-if="showControl.formItem.time.overTimeSelect"
            :date="syncItem.date"
            :value.sync="syncItem.time"
            :picker-options="{
              selectableRange: '00:00:00 - 23:59:59'
            }"
            placeholder="請選擇"
            :clearable="false"
            @change="onTimeChange(syncItem)"
          />
        </ElInputWrapper>
      </BaseElFormItem>
    </div>

    <BaseElFormItem prop="classTickets">
      <template #label>
        <div class="flex flex-col" style="gap: 4px">
          <FormItemTooltipLabel :label="displayData.formItemLabel.classTickets">
            <p>{{ displayData.formItemTooltip.classTicket }}</p>
          </FormItemTooltipLabel>
          <p v-if="showControl.formItemHint.classTicket" class="text-sm text-gray-60">{{ displayData.formItemHint.classTicket }}</p>
        </div>
      </template>
      <BaseElSelect
        v-model="syncItem.classTickets"
        testName="formData_service_classTicket"
        value-key="id"
        multiple
        :multiple-limit="1"
        @change="onClassTicketChange(syncItem)"
      >
        <BaseElSelectOption
          v-for="classTicket in displayClassTicketOptions(syncItem)"
          :key="classTicket.id"
          :label="classTicket.label"
          :disabled="classTicket.disabled"
          :value="classTicket"
        />
      </BaseElSelect>
    </BaseElFormItem>
  </BaseElForm>
</template>

<script>
import axios from 'axios'
import { useCreateAppointment } from '@/use/useCreateAppointment'
import { generateFormatPeriod } from '@/utils/time'
import { noEmptyRules } from '@/validation'
import { useVModel } from '@vueuse/core'
import { map, filter, get, intersection, uniq, includes, find, forEach, reduce, findIndex } from 'lodash'
import { formatDate, getMonth, thisMonth } from '@/utils/date'
import { computed, defineComponent, ref, reactive, nextTick, set, onMounted, onBeforeUnmount, watch, watchEffect } from 'vue'
import dayjs from '@/lib/dayjs'
import { CheckReservation } from '@/api/reservation'
import { serviceTypeConfig } from '@/config/reservation'
import { useShop } from '@/use/shop'
import FormItemTooltipLabel from '@/components/Form/FormItemTooltipLabel.vue'
import ElInputWrapper from '@/components/ElInputWrapper.vue'
import BaseTimePicker from '@/components/BaseTimePicker.vue'
import { availabilityEndpoints, useAvailabilityQuery } from '@/use/useAvailabilityQuery'
import { createAvailabilityPickerPanelBinding } from '@/use/useAvailabilityPickerPanel'

export default defineComponent({
  name: 'AppointmentPeriodForm',
  components: {
    FormItemTooltipLabel,
    ElInputWrapper,
    BaseTimePicker,
  },
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props, { emit }) {
    // 每個 AppointmentPeriodForm 持有自己的可用時段/日期狀態，
    // 避免共享 reactive object 造成任一 period 更新時所有 sibling re-render
    const dataPlaceholder = reactive({})
    const {
      cart,
      context,
      configData,
      memberData,
      getCartItemPreReservationsPayload,
      defaultSelectClassTicket,
      calculateCheckoutCashbackLimit,
      beginFlow,
      endFlow,
      presetData,
      updateCartItemData,
    } = useCreateAppointment()
    const syncItem = useVModel(props, 'item', emit)
    // 在 render 前初始化自己的 key，確保 displayAvailableTimes 首次求值即收集到
    // dataPlaceholder 依賴（若延後到 onMounted，後續 set 不會觸發 computed 更新）
    set(dataPlaceholder, syncItem.value.nanoId, {
      availableDate: [],
      availableTimes: [],
    })
    const { shopId } = useShop()
    const datePickerRef = ref(null)
    const availabilityCartItem = ref(null)
    const availabilityPayload = ref(null)
    const { query: availabilityQuery, cancel: cancelAvailability, getCachedData } = useAvailabilityQuery({
      endpoint: availabilityEndpoints.times,
      payload: availabilityPayload,
    })
    const availabilityPickerPanelBinding = createAvailabilityPickerPanelBinding()
    const mainPeriodCartItem = computed(() => get(cart.value, '[0].cartItem'))
    const formRef = ref(null)
    const formRules = computed(() => {
      const rules = {
        date: [noEmptyRules()],
        time: [noEmptyRules()],
      }

      const selectedService = get(mainPeriodCartItem.value, 'service')

      // 如果服務必選堂票
      if (get(selectedService, 'enableClassTicket') && get(selectedService, 'requiredClassTicket')) {
        rules.classTickets = [{ type: 'array', required: true, trigger: ['change', 'blur'], message: '請選擇堂票' }]
      }

      return rules
    })

    const showControl = computed(() => {
      const controls = {
        formItem: {
          time: {
            timeSelect: true,
            overTimeSelect: false,
          },
        },
        formItemHint: {
          classTicket: false,
        },
      }

      if (context.peopleCount > 1) {
        controls.formItemHint.classTicket = true
      }

      // 如果是加班時段
      if (syncItem.value.isOverTime) {
        controls.formItem.time.timeSelect = false
        controls.formItem.time.overTimeSelect = true
      }

      return controls
    })

    const displayData = computed(() => {
      return {
        mainServiceName: get(mainPeriodCartItem.value, 'service.name', '-'),

        formItemLabel: {
          isOverTime: '是否為加班時段',
          date: '預約日期',
          time: '預約時段',
          classTickets: '選擇堂票',
        },
        formItemTooltip: {
          classTicket: '僅顯示此會員擁有、並能用於已選擇的服務項目的堂票',
        },
        formItemHint: {
          classTicket: '多人預約時，選擇堂票僅會使用一張堂票',
        },
      }
    })

    // ?? 顯示的加班時段選項
    const displayOvertimeTimeOptions = computed(() => {
      return (cartItem) => {
        const offset = get(configData.reservation, 'timeUnitOffset')
        const timeUnit = get(configData.reservation, 'timeUnit')
        const selectedDate = formatDate(cartItem.date, 'YYYY/MM/DD')
        const times = generateFormatPeriod({ gap: timeUnit, offset, maxEnd: 1440 })
        return map(times, (time) => {
          return {
            label: time,
            value: dayjs(`${selectedDate} ${time}`).toDate(),
          }
        })
      }
    })

    // ?? 顯示的可用時段
    const displayAvailableTimes = computed(() => {
      const now = dayjs()
      const times = map(filter(get(dataPlaceholder, `${syncItem.value.nanoId}.availableTimes`), (item) => {
        return dayjs(item).isSame(syncItem.value.date, 'date')
      }), i => {
        return {
          label: dayjs(i).format('HH:mm'),
          value: i,
        }
      })
      const availableTimes = filter(times, (item) => {
        return dayjs(item.value).isAfter(now, 'minute')
      })

      return availableTimes
    })

    // 穩定 identity 的 picker-options：避免每次 render 產生新物件，
    // 觸發 el-date-picker 內部對 pickerOptions 的 deep watch → 開著的面板整塊重繪。
    // 物件必須為 reactive：el-date-picker 的 deep watch（picker.vue:864）只追蹤 reactive
    // 物件的內部 mutation；以下僅在 dataPlaceholder 變更時（availability 更新）重新指派
    // disabledDate function reference，觸發 deep watch → updateOptions → 面板重繪。
    // disabledDate 於執行時才讀取 reactive 的 dataPlaceholder / syncItem，指派時機不影響正確性。
    const datePickerOptionsRef = reactive({ disabledDate: () => true })
    const refreshDatePickerDisabledDate = () => {
      const availableTsSet = new Set(
        get(
          dataPlaceholder,
          `${syncItem.value.nanoId}.availableDate`,
          [],
        ),
      )
      // 非 hot path，Day.js 可讀性比較好
      const nowDayTs = dayjs().startOf('day').valueOf()
      datePickerOptionsRef.disabledDate = (date) => {
        if (syncItem.value.isOverTime) return false
        // hot path，使用 native Date
        const t = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
        ).getTime()
        return !availableTsSet.has(t) || t < nowDayTs
      }
    }
    refreshDatePickerDisabledDate()
    watch(dataPlaceholder, refreshDatePickerDisabledDate)
    const getDatePickerOptions = () => datePickerOptionsRef

    // ?? 顯示的堂票選項
    const displayClassTicketOptions = computed(() => {
      return (cartItem) => {
        const selectedService = mainPeriodCartItem.value.getDetailData('service')
        const appointmentDateTime = cartItem.time
        if (!selectedService) return []
        if (!appointmentDateTime) return []

        // 過濾已過期或無可用次數的堂票
        const availableClassTickets = filter(memberData.classTickets, (item) => {
          if (!item.availableUseTimes) return false
          if (item.isExp) {
            const expDate = item.exp
            if (!expDate) return true
            // 檢查堂票過期時間是否大於預約時段
            else if (dayjs(expDate).isAfter(dayjs(appointmentDateTime), 'date')) return true
          } else {
            return true
          }
        })

        // 過濾會員針對該服務適用的堂票
        const serviceClassTicketIds = map(selectedService.ClassTickets, 'id')
        const classTickets = map(availableClassTickets, 'ClassTicketId')
        const intersectionClassTickets = intersection(serviceClassTicketIds, uniq(classTickets))
        if (!intersectionClassTickets.length) return [] // 會員沒有可用於服務的堂票

        const finalAvailablesClassTickets = filter(availableClassTickets, (item) => includes(intersectionClassTickets, item.ClassTicketId))
        const preCartItem = []
        const curCartItemIdx = findIndex(cart.value, { id: cartItem.nanoId })
        for (let i = 0; i < curCartItemIdx; i++) {
          preCartItem.push(get(cart.value, `[${i}].cartItem`))
        }

        const formatedOptions = map(finalAvailablesClassTickets, (item) => {
        // 此張堂票已經被使用的次數
          const preUseTimes = reduce(preCartItem, (sum, i) => {
            const used = find(i.classTickets, { id: item.id })
            return sum + (used ? 1 : 0)
          }, 0)

          const expDate = item.exp ? formatDate(item.exp) : ''
          let label = `${item.name} 剩餘${item.availableUseTimes - preUseTimes}張`
          if (expDate) label = `${label} （期限：${expDate}）`

          return {
            ...item,
            label,
            disabled: item.availableUseTimes - preUseTimes <= 0,
          }
        })

        // 排序堂票，將 disabled: true 的選項排到最後，expDate 快過期的排在前面
        formatedOptions.sort((a, b) => {
          if (a.disabled && !b.disabled) return 1
          if (!a.disabled && b.disabled) return -1
          if (a.expDate && b.expDate) {
            if (dayjs(a.expDate).isBefore(b.expDate)) return -1
            if (dayjs(a.expDate).isAfter(b.expDate)) return 1
          }
          return 0
        })

        return formatedOptions
      }
    })

    const resetAvailability = (cartItem) => {
      cancelAvailability()
      availabilityCartItem.value = null
      availabilityPayload.value = null
      set(dataPlaceholder, cartItem.nanoId, {
        availableDate: [],
        availableTimes: [],
      })
    }

    const getPickerMonthRange = () => {
      const picker = get(datePickerRef.value, 'picker')
      if (picker) return getMonth(picker.year, picker.month + 1)
      return thisMonth()
    }

    // => 取得可預約時間
    const applyAvailabilityData = (res) => {
      const cartItem = availabilityCartItem.value
      if (!cartItem || !res || !res.times) return
      // 一次計算：dayjs 過濾過期時段並產生「本地時區 0 點 timestamp」Set。
      // 僅在 availability 更新時執行一次（數百筆 × 微秒級）；
      // disabledDate 查詢走 O(1) Set，面板 42 格重繪時不需任何 dayjs/物件分配。
      const now = dayjs()
      const dayTsSet = new Set()
      forEach(res.times, (item) => {
        if (!dayjs(item).isAfter(now, 'minute')) return // 過濾已過期時段
        dayTsSet.add(dayjs(item).startOf('day').valueOf())
      })

      set(dataPlaceholder, cartItem.nanoId, {
        availableDate: Array.from(dayTsSet),
        availableTimes: res.times,
      })
    }
    const getAvailableTimes = ({ cartItem, start, end }) => {
      const selectedServiceUnitId = get(cartItem.serviceUnit, 'id')
      const serviceType = cartItem.serviceType
      if (serviceType === serviceTypeConfig.humanService.value) {
        if (!selectedServiceUnitId) {
          resetAvailability(cartItem)
          return
        }
      }

      availabilityCartItem.value = cartItem
      set(dataPlaceholder, cartItem.nanoId, {
        availableDate: [],
        availableTimes: [],
      })
      const payload = {
        shopId: shopId.value,
        ...cartItem.getAvailableTimesPayload({
          start: start.toDate(),
          end: end.toDate(),
          notSpecifyUnits: get(configData, 'reservation.NotSpecifyUnits'),
          peopleCount: context.peopleCount,
          preReservations: getCartItemPreReservationsPayload(cartItem.nanoId),
        }),
      }
      availabilityPayload.value = payload
      applyAvailabilityData(getCachedData(payload))
    }

    watch(availabilityQuery.data, (res) => {
      applyAvailabilityData(res)
    })

    watch(availabilityQuery.error, (error) => {
      if (error && !axios.isCancel(error)) window.$message.error(error.message)
    })

    // => 更新可選預約日期
    const getDateOptions = async (cartItem) => {
      await nextTick()
      const loadPickerMonth = () => {
        const { start, end } = getPickerMonthRange()
        getAvailableTimes({ cartItem, start: dayjs(start), end: dayjs(end) })
      }

      availabilityPickerPanelBinding.bind(datePickerRef.value, () => {
        nextTick(loadPickerMonth)
      })
      loadPickerMonth()
    }

    // => 當日期變動時
    const onDateChange = (cartItem) => {
      cartItem.resetData(['time', 'classTickets'])
      cartItem.resetDetailData(['reservation'])
    }

    // => 當時間變動時
    const onTimeChange = async (cartItem) => {
      const seq = beginFlow()
      cartItem.resetData(['classTickets'])
      cartItem.resetDetailData(['reservation'])
      const [res, err] = await CheckReservation({
        shopId: shopId.value,
        ...cartItem.getPreDetailByStartPayload({
          notSpecifyUnits: get(configData.reservation, 'NotSpecifyUnits'),
          preReservations: getCartItemPreReservationsPayload(cartItem.nanoId),
          peopleCount: context.peopleCount,
        }),
      })
      if (err) {
        endFlow(seq)
        window.$message.error(err)
        return
      }
      cartItem.setDetailData('reservation', res)
      setTimeout(async () => {
        await nextTick()
        await defaultSelectClassTicket({
          displayClassTicketOptions: displayClassTicketOptions.value(cartItem),
          cartItem,
        })
        calculateCheckoutCashbackLimit()
      }, 0)
    }

    // => 當堂票變動時
    const onClassTicketChange = async (cartItem) => {
      const seq = beginFlow()
      cartItem.resetDetailData(['reservation'])
      const [res, err] = await CheckReservation({
        shopId: shopId.value,
        ...cartItem.getPreDetailByStartPayload({
          notSpecifyUnits: get(configData.reservation, 'NotSpecifyUnits'),
          preReservations: getCartItemPreReservationsPayload(cartItem.nanoId),
          peopleCount: context.peopleCount,
        }),
      })
      if (err) {
        endFlow(seq)
        window.$message.error(err)
        return
      }
      cartItem.setDetailData('reservation', res)
      setTimeout(async () => {
        await nextTick()
        calculateCheckoutCashbackLimit()
      }, 0)
    }

    // => 當是否為加班時段變動時
    const onIsOvertimeChange = (cartItem) => {
      resetAvailability(cartItem)
      cartItem.resetData(['date', 'time', 'classTickets'])
      cartItem.resetDetailData(['reservation'])
    }

    onMounted(() => {
      context.periodSettingFormRefs.push(formRef.value)
    })

    onBeforeUnmount(() => {
      cancelAvailability()
      availabilityPickerPanelBinding.unbind()
    })

    watchEffect(async () => {
      if (presetData.value && presetData.value.date) {
        const start = dayjs(presetData.value.date).startOf('day')
        const end = dayjs(presetData.value.date).endOf('day')
        await getAvailableTimes({ cartItem: syncItem.value, start: dayjs(start), end: dayjs(end) })
      }
    })

    return {
      formRef,
      syncItem,
      formRules,
      showControl,
      displayData,
      displayOvertimeTimeOptions,
      onTimeChange,
      onIsOvertimeChange,
      onDateChange,
      displayAvailableTimes,
      getDateOptions,
      getDatePickerOptions,
      displayClassTicketOptions,
      onClassTicketChange,
      datePickerRef,
    }
  },
})
</script>

<style lang="postcss" scoped>

</style>
